/**
 * ExtractedFieldEditor — formulaire d'override humain inline (9.T.5.v3.3
 * parent-agnostique + 9.T.5.v3.6 QA polish round 4 : router typé).
 *
 * Refacto propre v3.3 :
 *   - props `submissionId` REMPLACÉ par `{ parentKind, parentId }` — le
 *     dispatch d'invalidation est délégué au hook `useOverrideExtractedField`.
 *   - i18n namespace neutre `extracted_fields.edit.*`.
 *   - Aucune dépendance à `pages/submissions/*`.
 *
 * QA polish round 4 (9.T.5.v3.6) — router d'éditeur typé :
 *   - `insurer_codes` (canonique) → InsurerMultiSelect (registre dynamique 9.V.2c-2b)
 *   - `guarantee_codes` (canonique ou `_raw`) → GuaranteeMultiSelect
 *   - `field_key` finissant par `_raw` avec value array-of-strings →
 *     liste éditable chips (ajout/retrait sans JSON)
 *   - sinon → textarea JSON fallback (comportement legacy strict)
 *
 * Champs éditables (D38 figée) : `value`, `unit`, `needs_review`, `note`.
 * Champs NON éditables : `confidence`, `citation_*`.
 *
 * Layout containment QA v3.6 round 4 :
 *   - `<form>` racine : `min-w-0 max-w-full` → aucun enfant ne peut élargir
 *     le modal parent.
 *   - textarea JSON : wrap="soft" explicite + break-words.
 */

import type { ExtractedFieldRow, ExtractionParentKind } from "@kollectivia/shared";
import { GUARANTEES, type GuaranteeCode } from "@kollectivia/shared/constants";
import { useMemo, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import { useOverrideExtractedField } from "../../lib/queries/extracted-fields";
import { toast } from "../../lib/toast";
import { GuaranteeMultiSelect } from "../GuaranteeMultiSelect";
import { InsurerMultiSelect } from "../InsurerMultiSelect";

import { parseEditorValue, valueToEditorString } from "./extractedFieldEditorHelpers";

export type ExtractedFieldEditorProps = {
  field: ExtractedFieldRow;
  extractionId: string;
  parentKind: ExtractionParentKind;
  parentId: string;
  onClose: () => void;
};

// ---------------------------------------------------------------------------
// Field-key classification (QA polish round 4 — router typé)
// ---------------------------------------------------------------------------

type EditorMode = "insurers" | "guarantees" | "string_list" | "json_fallback";

function classifyFieldKey(fieldKey: string, value: unknown): EditorMode {
  // 9.T.5.v3.6 QA polish round 5 — priorité STRICTE : les variantes `_raw`
  // contiennent des valeurs libres (libellés bruts « Croix Bleue », « Humania »)
  // — dette V32-D. Elles routent TOUJOURS vers `StringListEditor` pour préserver
  // ces libellés au submit. Les codes canoniques `insurer_codes` / `guarantee_codes`
  // routent vers l'éditeur typé (InsurerMultiSelect dynamique / GuaranteeMultiSelect).
  // 9.V.2c-2b : InsurerMultiSelect ne filtre plus les codes hors registre — un
  // code inactif/inconnu reste visible et retirable (invariant anti-perte R1).
  if (fieldKey.endsWith("_raw") && Array.isArray(value)) return "string_list";
  if (fieldKey.endsWith("insurer_codes")) return "insurers";
  if (fieldKey.endsWith("guarantee_codes")) return "guarantees";
  return "json_fallback";
}

function coerceStringArray(v: unknown): string[] {
  if (Array.isArray(v)) {
    return v
      .filter((x) => typeof x === "string" || typeof x === "number")
      .map((x) => String(x).trim())
      .filter((x) => x.length > 0);
  }
  return [];
}

// ---------------------------------------------------------------------------
// Root editor form (state + submit path shared across editor variants)
// ---------------------------------------------------------------------------

export function ExtractedFieldEditor(props: ExtractedFieldEditorProps) {
  const { field, extractionId, parentKind, parentId, onClose } = props;
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const mutation = useOverrideExtractedField();

  const mode = useMemo(
    () => classifyFieldKey(field.field_key, field.value),
    [field.field_key, field.value],
  );

  // Typed states — pilotent l'un des sous-éditeurs; le fallback JSON reste
  // porté par `valueText`.
  // 9.V.2c-2b — aucun filtre registry ici : un code extrait inactif/inconnu
  // doit être préservé (invariant anti-perte R1). `InsurerMultiSelect` gère
  // l'affichage lisible + le fait qu'il ne soit pas réajoutable après retrait.
  const initialInsurers = useMemo<string[]>(() => {
    if (mode !== "insurers") return [];
    return coerceStringArray(field.value);
  }, [mode, field.value]);
  const initialGuarantees = useMemo<GuaranteeCode[]>(() => {
    if (mode !== "guarantees") return [];
    const registrySet = new Set<string>(GUARANTEES.map((g) => g.code));
    return coerceStringArray(field.value).filter((c): c is GuaranteeCode => registrySet.has(c));
  }, [mode, field.value]);
  const initialStringList = useMemo<string[]>(() => {
    if (mode !== "string_list") return [];
    return coerceStringArray(field.value);
  }, [mode, field.value]);

  const [insurerCodes, setInsurerCodes] = useState<string[]>(initialInsurers);
  const [guaranteeCodes, setGuaranteeCodes] = useState<GuaranteeCode[]>(initialGuarantees);
  const [stringList, setStringList] = useState<string[]>(initialStringList);

  const initialValueText = useMemo(() => valueToEditorString(field.value), [field.value]);
  const [valueText, setValueText] = useState<string>(initialValueText);

  const [unit, setUnit] = useState<string>(field.unit ?? "");
  const [needsReview, setNeedsReview] = useState<boolean>(field.needs_review);
  const [note, setNote] = useState<string>("");
  const [localError, setLocalError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLocalError(null);

    let nextValue: unknown;
    if (mode === "insurers") {
      nextValue = insurerCodes;
    } else if (mode === "guarantees") {
      nextValue = guaranteeCodes;
    } else if (mode === "string_list") {
      nextValue = stringList;
    } else {
      const parsed = parseEditorValue(valueText, field.value);
      if (!parsed.ok) {
        setLocalError(
          t(
            parsed.kind === "number"
              ? "extracted_fields.edit.error_invalid_number"
              : parsed.kind === "boolean"
                ? "extracted_fields.edit.error_invalid_boolean"
                : "extracted_fields.edit.error_invalid_json",
          ),
        );
        return;
      }
      nextValue = parsed.value;
    }

    const trimmedUnit = unit.trim();
    const trimmedNote = note.trim();

    try {
      await mutation.mutateAsync({
        fieldId: field.id,
        extractionId,
        parentKind,
        parentId,
        input: {
          value: nextValue,
          unit: trimmedUnit.length > 0 ? trimmedUnit : null,
          needs_review: needsReview,
          note: trimmedNote.length > 0 ? trimmedNote : null,
        },
      });
      toast.success(t("extracted_fields.edit.success"));
      onClose();
    } catch (err) {
      const message =
        err instanceof ApiException
          ? getApiErrorMessage(err, lang) || t("errors.generic")
          : t("errors.generic");
      toast.error(message);
    }
  }

  const submitting = mutation.isPending;

  return (
    <form
      onSubmit={onSubmit}
      className="mt-2 min-w-0 max-w-full space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3"
    >
      <div className="min-w-0">
        <label
          htmlFor={`ef-value-${field.id}`}
          className="block text-xs font-medium text-slate-600"
        >
          {t("extracted_fields.edit.value_label")}
        </label>

        {mode === "insurers" && (
          <div className="mt-1 min-w-0">
            <InsurerMultiSelect
              id={`ef-value-${field.id}`}
              value={insurerCodes}
              onChange={setInsurerCodes}
            />
            <p className="mt-1 text-[11px] text-slate-500">
              {t("extracted_fields.edit.typed_hint_insurers")}
            </p>
          </div>
        )}

        {mode === "guarantees" && (
          <div className="mt-1 min-w-0">
            <GuaranteeMultiSelect
              id={`ef-value-${field.id}`}
              value={guaranteeCodes}
              onChange={setGuaranteeCodes}
            />
            <p className="mt-1 text-[11px] text-slate-500">
              {t("extracted_fields.edit.typed_hint_guarantees")}
            </p>
          </div>
        )}

        {mode === "string_list" && (
          <StringListEditor
            id={`ef-value-${field.id}`}
            value={stringList}
            onChange={setStringList}
            disabled={submitting}
          />
        )}

        {mode === "json_fallback" && (
          <>
            <textarea
              id={`ef-value-${field.id}`}
              rows={Math.min(8, Math.max(2, valueText.split("\n").length))}
              wrap="soft"
              className="focus-visible:border-brand-500 focus-visible:ring-brand-500 mt-1 block w-full max-w-full break-all rounded border border-slate-300 bg-white p-2 font-mono text-xs text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
              aria-invalid={localError ? true : undefined}
              aria-describedby={localError ? `ef-value-error-${field.id}` : undefined}
              value={valueText}
              onChange={(e) => setValueText(e.target.value)}
              disabled={submitting}
            />
            <p className="mt-1 text-[11px] text-slate-500">
              {t(
                typeof field.value === "number"
                  ? "extracted_fields.edit.number_hint"
                  : typeof field.value === "boolean"
                    ? "extracted_fields.edit.boolean_hint"
                    : "extracted_fields.edit.value_hint",
              )}
            </p>
          </>
        )}

        {localError && (
          <p
            id={`ef-value-error-${field.id}`}
            className="mt-1 text-xs text-red-700"
            aria-live="polite"
          >
            {localError}
          </p>
        )}
      </div>

      <div className="flex min-w-0 flex-wrap gap-3">
        <div className="min-w-[140px] flex-1">
          <label
            htmlFor={`ef-unit-${field.id}`}
            className="block text-xs font-medium text-slate-600"
          >
            {t("extracted_fields.edit.unit_label")}
          </label>
          <input
            id={`ef-unit-${field.id}`}
            type="text"
            maxLength={20}
            className="focus-visible:border-brand-500 focus-visible:ring-brand-500 mt-1 w-full rounded border border-slate-300 bg-white p-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            disabled={submitting}
          />
        </div>

        <label className="mt-5 inline-flex items-center gap-2 text-xs text-slate-700">
          <input
            type="checkbox"
            checked={needsReview}
            onChange={(e) => setNeedsReview(e.target.checked)}
            disabled={submitting}
            className="focus-visible:ring-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
          />
          {t("extracted_fields.edit.needs_review_label")}
        </label>
      </div>

      <div className="min-w-0">
        <label htmlFor={`ef-note-${field.id}`} className="block text-xs font-medium text-slate-600">
          {t("extracted_fields.edit.note_label")}
        </label>
        <textarea
          id={`ef-note-${field.id}`}
          rows={2}
          wrap="soft"
          className="focus-visible:border-brand-500 focus-visible:ring-brand-500 mt-1 block w-full max-w-full break-words rounded border border-slate-300 bg-white p-2 text-xs text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
          placeholder={t("extracted_fields.edit.note_placeholder")}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={submitting}
        />
      </div>

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          className="btn-ghost focus-visible:ring-brand-500 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          onClick={onClose}
          disabled={submitting}
        >
          {t("extracted_fields.edit.cancel")}
        </button>
        <button
          type="submit"
          className="btn-primary focus-visible:ring-brand-500 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          disabled={submitting}
        >
          {submitting ? t("extracted_fields.edit.saving") : t("extracted_fields.edit.save")}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// StringListEditor — chips add/remove pour `_raw` array-of-strings.
// ---------------------------------------------------------------------------

function StringListEditor({
  id,
  value,
  onChange,
  disabled,
}: {
  id: string;
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<string>("");

  const commit = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (value.includes(trimmed)) {
      setDraft("");
      return;
    }
    onChange([...value, trimmed]);
    setDraft("");
  };

  return (
    <div className="mt-1 min-w-0">
      {value.length > 0 ? (
        <ul
          className="mb-2 flex flex-wrap gap-1.5"
          aria-label={t("extracted_fields.edit.raw_items_list_aria")}
        >
          {value.map((item, idx) => (
            <li
              key={`${item}-${idx}`}
              className="inline-flex max-w-full items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-xs text-slate-800"
            >
              <span className="min-w-0 break-all">{item}</span>
              <button
                type="button"
                onClick={() => onChange(value.filter((_, i) => i !== idx))}
                aria-label={t("extracted_fields.edit.raw_items_remove_aria", { item })}
                disabled={disabled}
                className="focus-visible:ring-brand-500 rounded-full text-slate-500 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-2 text-xs italic text-slate-500">
          {t("extracted_fields.edit.raw_items_empty")}
        </p>
      )}
      <div className="flex min-w-0 items-center gap-2">
        <input
          id={id}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            }
          }}
          disabled={disabled}
          placeholder={t("extracted_fields.edit.raw_items_placeholder")}
          className="focus-visible:border-brand-500 focus-visible:ring-brand-500 min-w-0 flex-1 rounded border border-slate-300 bg-white p-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
        />
        <button
          type="button"
          onClick={commit}
          disabled={disabled || draft.trim().length === 0}
          className="btn-ghost focus-visible:ring-brand-500 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        >
          {t("extracted_fields.edit.raw_items_add")}
        </button>
      </div>
    </div>
  );
}

// Re-export helpers pour compat tests (relocation neutre).
export { looksLikeJson, parseEditorValue } from "./extractedFieldEditorHelpers";
